import type { CSSProperties } from "react";
import type { Video } from "./types";

/**
 * Resolve the best HLS source URL from a Video. Prefers the master HLS
 * playlist (lets the player do ABR across all renditions) and falls back
 * to specific quality URLs only when the master is missing.
 *
 * Returns `null` when the user isn't entitled — backend gates the URL
 * fields server-side (see VideoLibrary::canApiAccess in the Laravel
 * repo). A null here is the caller's signal to render a paywall / locked
 * state instead of mounting the player.
 */
export function videoStreamUrl(
  v: Pick<Video, "masterSource" | "sources"> | null | undefined,
): string | null {
  if (!v) return null;
  return (
    v.masterSource ??
    v.sources?.q1080p ??
    v.sources?.q720p ??
    v.sources?.q480p ??
    v.sources?.q360p ??
    null
  );
}

/**
 * The shape a video is drawn in. Since 2026-09-09 the transcoder keeps the
 * source's aspect ratio and reports it as `width`/`height` (rotation
 * applied); every upload converted before that was resampled to exact 16:9
 * sizes and carries `null`, which the gotchas chapter ("Video orientation")
 * says to treat as 16:9. Zero, negative and non-finite dimensions get the same
 * treatment — a box is never drawn from garbage.
 */
export const DEFAULT_VIDEO_SHAPE: readonly [width: number, height: number] = [16, 9];

export function videoShape(
  v: Pick<Video, "width" | "height"> | null | undefined,
): readonly [width: number, height: number] {
  const w = v?.width;
  const h = v?.height;
  if (
    typeof w === "number" &&
    typeof h === "number" &&
    Number.isFinite(w) &&
    Number.isFinite(h) &&
    w > 0 &&
    h > 0
  )
    return [w, h];
  return DEFAULT_VIDEO_SHAPE;
}

/** CSS `aspect-ratio` terms — `"1080 / 1920"`. */
export function videoAspectRatio(v: Pick<Video, "width" | "height"> | null | undefined): string {
  const [w, h] = videoShape(v);
  return `${w} / ${h}`;
}

/** video.js's `aspectRatio` option — `"1080:1920"`. Handing the player the
 *  same ratio as its container means a fluid player reserves the right box at
 *  once; left to itself it assumes 16:9 until the manifest's metadata arrives,
 *  and a portrait stream visibly jumped. */
export function videoJsAspectRatio(v: Pick<Video, "width" | "height"> | null | undefined): string {
  const [w, h] = videoShape(v);
  return `${w}:${h}`;
}

/** The tallest a portrait or square player grows: the viewport, minus room
 *  for the page chrome. `svh` so a phone's collapsing URL bar does not resize
 *  the box mid-play. */
export const PORTRAIT_MAX_HEIGHT = "80svh";

/**
 * Inline style for a player box or one of its placeholders. Landscape keeps
 * filling its column as it always has; portrait and square are capped so the
 * box fits the viewport height and centred, because a 9:16 video drawn at a
 * 900 px column width would be 1600 px tall — a full-width column, not a
 * player. The wrappers' `overflow-hidden` and rounded corners are unaffected.
 */
export function videoBoxStyle(
  v: Pick<Video, "width" | "height"> | null | undefined,
): CSSProperties {
  const [w, h] = videoShape(v);
  const style: CSSProperties = { aspectRatio: `${w} / ${h}` };
  if (h >= w) {
    style.width = `min(100%, calc(${PORTRAIT_MAX_HEIGHT} * ${w} / ${h}))`;
    style.marginInline = "auto";
  }
  return style;
}
